/**
 * Option lists for the two scalar classification facets in the prospect filter.
 *
 * Both vocabularies are **fixed** — three levels, three JC years — so they are static
 * lists rather than a `GetDistinct...Async` endpoint. Grad Year needs a distinct-values
 * call because grad years are open-ended; these do not, and no distinct-values endpoint
 * exists for anything else.
 *
 * `value` is what lands in `TagFilters` and goes on the wire; `label` is what a coach
 * reads. They differ here — Level stores the ordinal `'1'` and reads "JUCO" — which is
 * why `FilterChip` takes a separate label.
 *
 * Mobile's equivalents are `PLAYER_LEVEL_OPTIONS` and `JUCO_YEAR_OPTIONS` in
 * `mobile/constants/player-classification.ts`, which already carry this exact shape.
 */

import { PLAYER_LEVELS, formatPlayerLevel } from "./playerLevels";
import { JUCO_YEARS, formatJucoYear } from "../utils/roster-class";

export interface FilterFacetOption {
  /** Stored in `TagFilters` and sent on the wire. */
  value: string;
  /** What the coach reads. */
  label: string;
}

export const PLAYER_LEVEL_FILTER_OPTIONS: readonly FilterFacetOption[] =
  PLAYER_LEVELS.map((level) => ({
    // String, not the ordinal: `TagFilters` is `Record<string, string[]>`, and High
    // School is ordinal 0 — a number here would be dropped by any truthiness check on
    // the way to the query string.
    value: String(level),
    label: formatPlayerLevel(level) ?? String(level),
  }));

export const JUCO_YEAR_FILTER_OPTIONS: readonly FilterFacetOption[] = JUCO_YEARS.map(
  (year) => ({
    value: year,
    label: formatJucoYear(year) ?? `JC ${year}`,
  }),
);

/**
 * Why the JC Year facet is inert, shown beside it rather than in place of it.
 *
 * The server ORs within a facet and ANDs across facets, so a JC Year picked without
 * Level = JUCO matches nobody. Saying so beats a control that silently returns an empty
 * list, and beats hiding the facet, which leaves a coach hunting for it.
 */
export const JUCO_YEAR_FACET_HINT = "Select Level: JUCO to filter by JC year";
